/**
 * Which of our videos a PDP page carries, from the page's point of view.
 *
 * A page row (`desires_pdp_urls`) carries any number of our videos, one link
 * each (`desires_pdp_url_videos`, TDC-1644 — a retailer page can host two of
 * ours). Each link owns the file URL the retailer serves for that video, the
 * Google overrides and that video's VideoObject; the page's score is the
 * page's, and shows on every video linked to it. The settings modal's chips,
 * the score panel's strip and the product panel's cards all read the links
 * through here, so they cannot disagree about whose page it is.
 */
export type PageAssignment = "mine" | "unassigned" | "other";

export interface AssignableLink {
  s3_file_id: number;
}

export interface AssignableRow {
  videos?: AssignableLink[] | null;
}

/** This video's link on the page, when it is on it. */
export const linkFor = <L extends AssignableLink>(
  row: { videos?: L[] | null } | null | undefined,
  videoId?: number | null,
): L | null => {
  if (videoId == null || !row?.videos) return null;
  return row.videos.find((l) => Number(l.s3_file_id) === Number(videoId)) || null;
};

/**
 * "mine" — this video is on the page; "unassigned" — no video is;
 * "other" — only other videos are (this one can still be added alongside).
 */
export const pageAssignment = (row: AssignableRow, videoId?: number | null): PageAssignment => {
  const links = row.videos || [];
  if (links.length === 0) return "unassigned";
  if (linkFor(row, videoId)) return "mine";
  return "other";
};

/**
 * Where the Product Pages tab lands after a load: the first page that carries
 * this video, else the first one no video is on, else the first tab. Rows keep
 * their `id ASC` order — the selection is by index and three refresh paths rely
 * on that order surviving — so this is the only place status affects position.
 */
export const defaultPageIndex = (rows: AssignableRow[], videoId?: number | null): number => {
  const mine = rows.findIndex((r) => pageAssignment(r, videoId) === "mine");
  if (mine >= 0) return mine;
  const free = rows.findIndex((r) => pageAssignment(r, videoId) === "unassigned");
  return free >= 0 ? free : 0;
};

/**
 * Deep link to a video's detail view — the admin videos page for internal
 * staff, the brand portal's TDC videos page otherwise. The product settings
 * panel's rule, lifted here so both surfaces resolve the same way. Takes the
 * result of `isInternal(role)` rather than the role: this module stays free of
 * `src/utils`, which drags in @react-pdf at import time and cannot load in
 * the test runner.
 */
export const pdpVideoHref = (internal: boolean, videoId?: number | null): string | null => {
  if (!videoId) return null;
  return internal ? `/videos?video=${videoId}` : `/videos/tdc?video=${videoId}`;
};
